import { supabase } from '../lib/supabaseClient';

export interface DynamicQRInput {
  short_code: string;
  original_url: string;
  title: string;
  description?: string;
  expires_at?: string | null;
  password?: string | null;
  customization?: any;
}

export interface DynamicQRRow {
  id: number;
  user_id: string;
  short_code: string;
  original_url: string;
  title: string;
  description?: string;
  is_active: boolean;
  expires_at?: string;
  password?: string;
  customization: any;
  analytics: any;
  created_at: string;
  updated_at: string;
}

export async function createDynamicQR(input: DynamicQRInput): Promise<DynamicQRRow> {
  const { data: userData } = await supabase.auth.getUser();
  
  if (!userData.user) {
    throw new Error('User not authenticated');
  }

  const { data, error } = await supabase
    .from('dynamic_qr')
    .insert([
      {
        user_id: userData.user.id,
        short_code: input.short_code,
        original_url: input.original_url,
        title: input.title,
        description: input.description ?? null,
        expires_at: input.expires_at ?? null,
        password: input.password ?? null,
        customization: input.customization ?? {},
        analytics: { totalScans: 0, uniqueScans: 0 },
      },
    ])
    .select()
    .single();

  if (error) throw error;
  return data as DynamicQRRow;
}

export async function listMyDynamicQR(limit = 20, offset = 0): Promise<DynamicQRRow[]> {
  const { data, error } = await supabase
    .from('dynamic_qr')
    .select('id, short_code, original_url, title, is_active, created_at, analytics')
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (error) throw error;
  return data as DynamicQRRow[];
}

export async function getDynamicQR(short_code: string): Promise<DynamicQRRow> {
  const { data, error } = await supabase
    .from('dynamic_qr')
    .select('*')
    .eq('short_code', short_code)
    .single();

  if (error) throw error;
  return data as DynamicQRRow;
}

export async function updateDynamicQR(
  short_code: string,
  updates: Partial<{
    original_url: string;
    title: string;
    description: string | undefined;
    is_active: boolean;
    expires_at: string | undefined;
    password: string | undefined;
    customization: any;
  }>
): Promise<DynamicQRRow> {
  const { data, error } = await supabase
    .from('dynamic_qr')
    .update(updates)
    .eq('short_code', short_code)
    .select()
    .single();

  if (error) throw error;
  return data as DynamicQRRow;
}

export async function deleteDynamicQR(short_code: string): Promise<void> {
  const { error } = await supabase.from('dynamic_qr').delete().eq('short_code', short_code);

  if (error) throw error;
}

export async function getQRAnalytics(short_code: string, period = '7d') {
  // Calculate date range
  const daysMap: { [key: string]: number } = { '7d': 7, '30d': 30, '90d': 90 };
  const days = daysMap[period] || 7;
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - days);

  const { data: qr, error: qrError } = await supabase
    .from('dynamic_qr')
    .select('id, short_code, title, original_url, created_at')
    .eq('short_code', short_code)
    .single();

  if (qrError) throw qrError;

  const { data: scans, error: scansError } = await supabase
    .from('scan_events')
    .select('*')
    .eq('short_code', short_code)
    .gte('scanned_at', startDate.toISOString())
    .order('scanned_at', { ascending: false });

  if (scansError) throw scansError;

    // Process analytics
    const totalScans = scans.length;
    const uniqueIPs = new Set(scans.map((s) => s.ip_address));
    const uniqueScans = uniqueIPs.size;

    // Group by date
    const scansByDate: { [key: string]: number } = {};
    const scansByCountry: { [key: string]: number } = {};
    const scansByDevice: { [key: string]: number } = {};
    const scansByBrowser: { [key: string]: number } = {};

    scans.forEach((scan) => {
      const date = new Date(scan.scanned_at).toISOString().split('T')[0];
      scansByDate[date] = (scansByDate[date] || 0) + 1;

      const country = scan.country || 'Unknown';
      scansByCountry[country] = (scansByCountry[country] || 0) + 1;

      const device = (scan.device && typeof scan.device === 'object' && scan.device.device) || 'desktop';
      scansByDevice[device] = (scansByDevice[device] || 0) + 1;

      const browser = (scan.device && typeof scan.device === 'object' && scan.device.browser) || 'Unknown';
      scansByBrowser[browser] = (scansByBrowser[browser] || 0) + 1;
    });

    return {
      qrCode: qr,
      summary: {
        totalScans,
        uniqueScans,
        period,
      },
      charts: {
        scansByDate: Object.entries(scansByDate).map(([date, scans]) => ({ date, scans })),
        scansByCountry: Object.entries(scansByCountry)
          .map(([country, scans]) => ({ country, scans }))
          .sort((a, b) => b.scans - a.scans),
        scansByDevice: Object.entries(scansByDevice).map(([device, scans]) => ({ device, scans })),
        scansByBrowser: Object.entries(scansByBrowser)
          .map(([browser, scans]) => ({ browser, scans }))
          .sort((a, b) => b.scans - a.scans),
      },
      recentScans: scans.slice(0, 20),
    };
}




